import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import "./EventRegistrationPage.css";

import EventDetails from "./EventDetails";
import RegistrationForm from "./RegistrationForm";
import PaymentView from "./PaymentView";

const API_URL = process.env.REACT_APP_API_URL;

export default function EventRegistrationPage() {
  const { eventId } = useParams();

  // =====================================================
  // STATES
  // =====================================================

  const [step, setStep] = useState("details");

  const [contact, setContact] = useState("");
  const [otp, setOtp] = useState("");

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [resendTimer, setResendTimer] = useState(0);

  const [userData, setUserData] = useState(null);

  const [formErrors, setFormErrors] = useState({});

  const [showOtp, setShowOtp] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isPaying, setIsPaying] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Holds the REAL backend registration response (array, as returned
  // by POST /event-registrations/event/{eventId}). PaymentView's own
  // useMemo normalizer already knows how to unwrap an array, an
  // object, or a { registrations: [...] } envelope, so this is passed
  // through untouched — nothing here re-shapes or guesses at fields.
  const [registration, setRegistration] = useState(null);

  // =====================================================
  // EVENT DATA
  // =====================================================

  const [eventData, setEventData] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState("");

  // =====================================================
  // FORM DATA
  // =====================================================

  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    nationality: "",
    memberStatus: "non-member",
    dietaryRestrictions: "",
    dietaryNote: "No",
    accompanyingPersons: [],
    vatInvoice: "no",
    additionalInfo: "",
    guestCount: 1,
  });

  // =====================================================
  // GET EVENT DETAILS
  // GET /events/{event_id}
  // =====================================================

  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!eventId) {
        setEventError("Event ID is missing.");
        setEventLoading(false);
        return;
      }

      setEventLoading(true);
      setEventError("");

      try {
        console.log("Fetching event:", eventId);

        const response = await fetch(`${API_URL}/events/${eventId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch event. Status: ${response.status}`);
        }

        const result = await response.json();

        console.log("Event Details API Response:", result);

        if (!result.success || !result.data) {
          throw new Error("Event details not found.");
        }

        const apiEvent = result.data;

        // =================================================
        // MAP API RESPONSE TO UI FORMAT
        // =================================================

        const formattedEvent = {
          id: apiEvent.id,

          title: apiEvent.event_title || "",

          category: apiEvent.event_category || "",

          date: apiEvent.event_date || "",

          checkin_time: apiEvent.checkin_time || "",

          dinner_time: apiEvent.dinner_time || "",

          venue: apiEvent.venue || "",

          status: apiEvent.status || "",

          capacity: apiEvent.capacity || 0,

          member_price: apiEvent.member_price || 0,

          guest_price: apiEvent.non_member_price || 0,

          description: apiEvent.description || "",

          booking_deadline: apiEvent.booking_deadline || "",

          dress_code: apiEvent.dress_code || "",

          banner_image: apiEvent.banner_image || "",

          whats_included: apiEvent.whats_included || "",

          special_guest: apiEvent.special_guest || "",

          menu: apiEvent.menu || "",

          special_notes: apiEvent.special_notes || "",

          approval_status: apiEvent.approval_status || "",

          created_by: apiEvent.created_by,

          created_date: apiEvent.created_date,

          updated_date: apiEvent.updated_date,

          vendors: apiEvent.vendors || [],

          activities: apiEvent.activities || [],

          images: apiEvent.images || [],

          approvals: apiEvent.approvals || [],

          // UI fields that API doesn't directly provide
          day: getDayName(apiEvent.event_date),

          currency: "$",

          venue_city: apiEvent.venue || "",

          highlights: createHighlights(apiEvent),
        };

        console.log("Formatted Event:", formattedEvent);

        setEventData(formattedEvent);
      } catch (err) {
        console.error("Event Details Error:", err);

        setEventError(
          err.message || "Unable to load event details. Please try again.",
        );
      } finally {
        setEventLoading(false);
      }
    };

    fetchEventDetails();
  }, [eventId]);

  // =====================================================
  // GET DAY NAME
  // =====================================================

  const getDayName = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString("en-US", {
      weekday: "long",
    });
  };

  // =====================================================
  // CREATE HIGHLIGHTS
  // =====================================================

  const createHighlights = (event) => {
    const highlights = [];

    if (event.capacity) {
      highlights.push({
        label: `${event.capacity} Guests`,
        sub: "Event Capacity",
      });
    }

    if (event.whats_included) {
      highlights.push({
        label: "What's Included",
        sub: event.whats_included,
      });
    }

    if (event.special_guest) {
      highlights.push({
        label: "Special Guest",
        sub: event.special_guest,
      });
    }

    if (event.menu) {
      highlights.push({
        label: "Menu",
        sub: event.menu,
      });
    }

    return highlights;
  };

  // =====================================================
  // HELPER: DETECT CONTACT TYPE
  // =====================================================

  const isEmailContact = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  };

  const isPhoneContact = (value) => {
    const digits = value.replace(/\D/g, "");
    return digits.length >= 8;
  };

  // =====================================================
  // SEND EMAIL OTP
  // POST /auth/email/send-otp
  // =====================================================

  const sendEmailOtp = async (email) => {
    console.log("Sending Email OTP to:", email);

    const response = await fetch(`${API_URL}/auth/email/send-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const result = await response.json();

    console.log("Send Email OTP Response:", result);

    if (!response.ok) {
      throw new Error(result.message || "Failed to send OTP to email.");
    }

    return result;
  };

  // =====================================================
  // VERIFY EMAIL OTP
  // POST /auth/email/verify-otp
  // =====================================================

  const verifyEmailOtp = async (email, otpCode) => {
    console.log("Verifying Email OTP for:", email);

    const response = await fetch(`${API_URL}/auth/email/verify-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, otp: otpCode }),
    });

    const result = await response.json();

    console.log("Verify Email OTP Response:", result);

    if (!response.ok) {
      throw new Error(result.message || "Invalid OTP. Please try again.");
    }

    return result;
  };

  // =====================================================
  // VERIFY PHONE (SEND + VERIFY)
  // POST /auth/phone/verify
  //
  // Sends OTP when no otpCode is passed.
  // Verifies OTP when otpCode is passed.
  // =====================================================

  const verifyPhone = async (phone, otpCode) => {
    console.log(
      otpCode ? "Verifying Phone OTP for:" : "Sending Phone OTP to:",
      phone,
    );

    const body = { phone };

    if (otpCode) {
      body.otp = otpCode;
    }

    const response = await fetch(`${API_URL}/auth/phone/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    const result = await response.json();

    console.log("Verify Phone Response:", result);

    if (!response.ok) {
      throw new Error(result.message || "Phone verification failed.");
    }

    return result;
  };

  // =====================================================
  // FETCH MEMBER PROFILE
  // GET /members/{member_id}
  //
  // ⚠️ Adjust the endpoint if your backend uses a different path
  // (e.g. /members/me, /auth/me, /member/{id})
  // =====================================================

  const fetchMemberProfile = async (memberId) => {
    const token = localStorage.getItem("access_token");

    const response = await fetch(`${API_URL}/members/${memberId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    const raw = await response.json();

    console.log("RAW /members response:", raw);

    if (!response.ok) {
      throw new Error(raw.message || "Failed to fetch member profile.");
    }

    // =============================================
    // UNWRAP: handle all common envelope shapes
    // =============================================
    // Case 1: { success, data: { ...member } }
    // Case 2: { success, data: { member: { ... } } }
    // Case 3: { success, data: { members: [ { ... } ] } }
    // Case 4: direct member object { id, first_name, ... }
    // =============================================

    let member = raw;

    if (raw && typeof raw === "object") {
      if (raw.data !== undefined) {
        member = raw.data;
      } else if (raw.member !== undefined) {
        member = raw.member;
      } else if (Array.isArray(raw.members) && raw.members.length > 0) {
        member = raw.members[0];
      }
    }

    // If data itself has a nested `member`
    if (member && typeof member === "object" && member.member) {
      member = member.member;
    }

    // If data is an array, take first
    if (Array.isArray(member)) {
      member = member[0];
    }

    console.log("Normalized member:", member);

    if (!member || typeof member !== "object") {
      throw new Error("Member profile could not be parsed from response.");
    }

    // =============================================
    // NORMALIZE FIELD NAMES
    // =============================================
    return {
      id: member.id ?? member.member_id ?? memberId,

      title: member.title ?? member.member_title ?? member.salutation ?? "",

      first_name:
        member.first_name ??
        member.firstName ??
        member.firstname ??
        member.given_name ??
        "",

      last_name:
        member.last_name ??
        member.lastName ??
        member.lastname ??
        member.surname ??
        member.family_name ??
        "",

      email: member.email ?? member.email_address ?? "",

      phone:
        member.phone ??
        member.mobile ??
        member.mobile_number ??
        member.phone_number ??
        member.contact_number ??
        "",

      nationality:
        member.nationality ?? member.country ?? member.country_name ?? "",

      member_id: member.member_id ?? member.id ?? memberId,

      is_member: member.is_member ?? true,

      registration_number:
        member.registration_number ?? member.member_number ?? "",

      // keep the raw member around in case RegistrationForm needs more fields
      _raw: member,
    };
  };

  // =====================================================
  // RESEND TIMER
  // =====================================================

  const startResendTimer = () => {
    setResendTimer(30);

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  // =====================================================
  // REGISTER / SEND OTP (Book Now handler)
  // =====================================================

  const handleRegisterClick = async (e) => {
    e.preventDefault();

    setError("");
    setShowOtp(false);

    const trimmedContact = contact?.trim();

    if (!trimmedContact) {
      setError("Please enter your mobile or email to continue");
      return;
    }

    const isEmail = isEmailContact(trimmedContact);
    const isPhone = isPhoneContact(trimmedContact);

    if (!isEmail && !isPhone) {
      setError("Please enter a valid email or phone number");
      return;
    }

    setIsLoading(true);
    setStep("checking");

    try {
      if (isEmail) {
        // =============================================
        // EMAIL FLOW → POST /auth/email/send-otp
        // =============================================
        await sendEmailOtp(trimmedContact);

        setUserData(null);
        setFormData((prev) => ({
          ...prev,
          email: trimmedContact,
          phone: "",
        }));
      } else {
        // =============================================
        // PHONE FLOW → POST /auth/phone/verify (send)
        // =============================================
        await verifyPhone(trimmedContact);

        setUserData(null);
        setFormData((prev) => ({
          ...prev,
          phone: trimmedContact,
          email: "",
        }));
      }

      setShowOtp(true);
      startResendTimer();
      setError("");
      setStep("details");
    } catch (err) {
      console.error("Send OTP Error:", err);
      setError(err.message || "Something went wrong. Please try again.");
      setStep("details");
    } finally {
      setIsLoading(false);
    }
  };

  // =====================================================
  // VERIFY OTP
  //
  // Member status is read directly from the auth response's
  // `is_member` field (requirement 1) — the downstream price
  // selection (member_price vs non_member_price) happens later,
  // inside RegistrationForm, using this same memberStatus value.
  // =====================================================

  const handleOtpVerify = async (e) => {
    e.preventDefault();

    setError("");

    if (!otp || otp.length < 4) {
      setError("Please enter the OTP code");
      return;
    }

    setIsLoading(true);

    const trimmedContact = contact?.trim();
    const isEmail = isEmailContact(trimmedContact);

    try {
      let result;

      if (isEmail) {
        // =============================================
        // VERIFY EMAIL OTP → POST /auth/email/verify-otp
        // =============================================
        result = await verifyEmailOtp(trimmedContact, otp);
      } else {
        // =============================================
        // VERIFY PHONE OTP → POST /auth/phone/verify (verify)
        // =============================================
        result = await verifyPhone(trimmedContact, otp);
      }

      console.log("OTP Verify Result:", result);

      // =============================================
      // READ THE AUTH RESPONSE
      // Expected shape:
      // { authenticated, is_member, member_id, registration_number, access_token }
      // =============================================
      const authenticated = result.authenticated === true;
      const isMember = result.is_member === true;
      const memberId = result.member_id ?? null;
      const registrationNumber = result.registration_number || "";
      const accessToken = result.access_token || "";

      if (!authenticated) {
        setError("Authentication failed. Please try again.");
        return;
      }

      // =============================================
      // STORE TOKEN FOR LATER API CALLS
      // =============================================
      if (accessToken) {
        localStorage.setItem("access_token", accessToken);
      }

      setShowOtp(false);
      setOtp("");
      if (isMember && memberId) {
        try {
          const memberProfile = await fetchMemberProfile(memberId);

          console.log("Normalized Member Profile:", memberProfile);

          setUserData({
            id: memberProfile.member_id,
            isMember: true,
            memberId: memberProfile.member_id,
            registrationNumber:
              memberProfile.registration_number || registrationNumber,
            firstName: memberProfile.first_name,
            lastName: memberProfile.last_name,
            email: memberProfile.email || trimmedContact,
            phone: memberProfile.phone,
            nationality: memberProfile.nationality,
            title: memberProfile.title,
            memberStatus: "member",
            raw: memberProfile._raw,
          });

          setFormData((prev) => ({
            ...prev,
            title: memberProfile.title || prev.title,
            firstName: memberProfile.first_name || prev.firstName,
            lastName: memberProfile.last_name || prev.lastName,
            email: memberProfile.email || prev.email || trimmedContact,
            phone: memberProfile.phone || prev.phone,
            nationality: memberProfile.nationality || prev.nationality,
            memberStatus: "member",
          }));

          setStep("memberForm");
        } catch (profileErr) {
          console.error("Member profile fetch failed:", profileErr);

          // Fallback: still allow memberForm with what we have
          setUserData({
            id: memberId,
            isMember: true,
            memberId: memberId,
            registrationNumber: registrationNumber,
            email: isEmail ? trimmedContact : "",
            phone: !isEmail ? trimmedContact : "",
            memberStatus: "member",
          });

          setFormData((prev) => ({
            ...prev,
            email: isEmail ? trimmedContact : prev.email,
            phone: !isEmail ? trimmedContact : prev.phone,
            memberStatus: "member",
          }));

          setStep("memberForm");
        }
      } else {
        // =============================================
        // NEW GUEST / NON-MEMBER
        // =============================================
        setUserData({
          isMember: false,
          registrationNumber: registrationNumber,
        });

        setFormData((prev) => ({
          ...prev,
          email: isEmail ? trimmedContact : "",
          phone: !isEmail ? trimmedContact : "",
          memberStatus: "non-member",
        }));

        setStep("guestForm");
      }
    } catch (err) {
      console.error("OTP Verify Error:", err);
      setError(err.message || "OTP verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // =====================================================
  // RESEND OTP
  // =====================================================

  const handleResendOtp = async () => {
    try {
      setError("");

      const trimmedContact = contact?.trim();
      const isEmail = isEmailContact(trimmedContact);

      if (isEmail) {
        await sendEmailOtp(trimmedContact);
      } else {
        await verifyPhone(trimmedContact);
      }

      startResendTimer();
    } catch (err) {
      console.error("Resend OTP Error:", err);
      setError(err.message || "Failed to resend OTP");
    }
  };

  // =====================================================
  // VALIDATE FORM
  // =====================================================

  const validateForm = () => {
    const errors = {};

    const required = ["firstName", "lastName", "email", "phone"];

    required.forEach((field) => {
      if (!String(formData[field] || "").trim()) {
        errors[field] = "This field is required";
      }
    });

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  // =====================================================
  // FORM SUBMIT
  //
  // Called by RegistrationForm AFTER it has already POSTed to the
  // real registration API (POST /event-registrations/event/{eventId})
  // and received the backend's response. This function's only job is
  // to store that real response and advance to the payment step.
  //
  // It must NOT create a fake registration object and must NOT call
  // any API itself — the real call already happened once, inside
  // RegistrationForm.submitRegistration (requirement 12: no duplicate
  // calls).
  // =====================================================

  const handleFormSubmit = (e, registrationPaymentData) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    setError("");

    const backendRegistrations = registrationPaymentData?.registrations;

    if (
      !Array.isArray(backendRegistrations) ||
      backendRegistrations.length === 0
    ) {
      setError("Registration was not created correctly. Please try again.");
      return;
    }

    /*
     * Store the RAW backend response array. PaymentView's own
     * useMemo normalizer already knows how to unwrap:
     *   - a registration array   → registration[0]
     *   - a registration object  → itself
     *   - { registrations: [] }  → registrations[0]
     *   - { registration: {} }   → registration
     *
     * so this is passed through untouched. registration.id,
     * registration.order_id, registration.event_id, registration.price,
     * registration.currency, and registration.payment_status all come
     * straight from the backend — nothing here re-shapes or guesses
     * at any of those fields.
     */
    setRegistration(backendRegistrations);
    setUserData(registrationPaymentData.mainRegistration || null);
    setStep("payment");
  };

  // =====================================================
  // UPDATE FORM
  // =====================================================

  const updateForm = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // =====================================================
  // TOTAL PRICE
  //
  // Still used by PaymentView as a fallback (getTotalUSD prop) for
  // cases where registration.price isn't available yet — but once a
  // real registration exists, PaymentView prefers registration.price
  // over this.
  // =====================================================

  const getTotalUSD = () => {
    if (!eventData) {
      return 0;
    }

    const isMember = userData?.isMember || formData.memberStatus === "member";

    const price = isMember
      ? Number(eventData.member_price || 0)
      : Number(eventData.guest_price || 0);

    return price * Number(formData.guestCount || 1);
  };

  // =====================================================
  // PAYPAL PAYMENT
  // =====================================================

  const handlePayPalPayment = async () => {
    setError("");
    setIsPaying(true);

    try {
      const amountUSD = getTotalUSD();

      const reference = `${userData?.firstName || ""} ${
        userData?.lastName || ""
      }`;

      console.log(`PayPal order: $${amountUSD} — ref: ${reference}`);

      // TODO: Replace with real PayPal API
      await new Promise((resolve) => setTimeout(resolve, 900));

      setPaymentConfirmed(true);
    } catch (err) {
      console.error(err);

      setError("Something went wrong starting PayPal checkout.");
    } finally {
      setIsPaying(false);
    }
  };

  // =====================================================
  // BANK TRANSFER
  // =====================================================

  const handleBankTransferConfirm = async () => {
    setError("");
    setIsPaying(true);

    try {
      const amountUSD = getTotalUSD();

      const amountEUR = (amountUSD * 0.92).toFixed(2);

      const reference = `${userData?.firstName || ""} ${
        userData?.lastName || ""
      }`;

      console.log(
        `Bank transfer: $${amountUSD} / €${amountEUR} — ${reference}`,
      );

      // TODO: Replace with real bank transfer API
      await new Promise((resolve) => setTimeout(resolve, 700));

      setPaymentConfirmed(true);
    } catch (err) {
      console.error(err);

      setError("Could not record your bank transfer. Please try again.");
    } finally {
      setIsPaying(false);
    }
  };

  // =====================================================
  // ACCOMPANYING PERSON
  // =====================================================

  const addAccompanyingPerson = () => {
    setFormData((prev) => ({
      ...prev,

      accompanyingPersons: [
        ...prev.accompanyingPersons,

        {
          id: Date.now(),
          fullName: "",
          email: "",
          mealPreference: "",
        },
      ],
    }));
  };

  const removeAccompanyingPerson = (id) => {
    setFormData((prev) => ({
      ...prev,

      accompanyingPersons: prev.accompanyingPersons.filter(
        (person) => person.id !== id,
      ),
    }));
  };

  const updateAccompanyingPerson = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,

      accompanyingPersons: prev.accompanyingPersons.map((person) =>
        person.id === id
          ? {
              ...person,
              [field]: value,
            }
          : person,
      ),
    }));
  };

  // =====================================================
  // LOADING SPINNER
  // =====================================================

  const LoadingSpinner = () => (
    <div className="er-loading">
      <div className="er-spinner"></div>

      <p>Loading event details...</p>
    </div>
  );

  // =====================================================
  // NAVBAR
  // =====================================================

  const NavBar = () => (
    <nav className="er-nav">
      <div className="er-nav-brand">
        <div className="er-nav-logo">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="18" stroke="#bfa15f" strokeWidth="1.5" />

            <path
              d="M20 4 L28 10 L28 22 C28 30 20 36 20 36 C20 36 12 30 12 22 L12 10 L20 4Z"
              stroke="#bfa15f"
              strokeWidth="1.2"
            />
          </svg>
        </div>

        <div className="er-nav-brand-text">
          <span className="er-nav-brand-top">LA CHAÎNE</span>

          <span className="er-nav-brand-bot">DES RÔTISSEURS</span>
        </div>
      </div>
    </nav>
  );

  // =====================================================
  // FOOTER
  // =====================================================

  const Footer = () => (
    <footer className="er-footer">
      <div className="er-footer-content"></div>
    </footer>
  );

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="er-root">
      <NavBar />

      <main className="er-main">
        <div className="er-container">
          {/* EVENT LOADING */}
          {eventLoading && <LoadingSpinner />}

          {/* EVENT ERROR */}
          {!eventLoading && eventError && (
            <div className="er-loading">
              <p>{eventError}</p>
            </div>
          )}

          {/* EVENT DETAILS */}
          {!eventLoading && !eventError && eventData && step === "details" && (
            <EventDetails
              EVENT={eventData}
              contact={contact}
              setContact={setContact}
              isLoading={isLoading}
              error={error}
              onRegisterClick={handleRegisterClick}
              showOtp={showOtp}
              otp={otp}
              setOtp={setOtp}
              handleOtpVerify={handleOtpVerify}
              resendTimer={resendTimer}
              handleResendOtp={handleResendOtp}
            />
          )}

          {/* CHECKING */}
          {!eventLoading && !eventError && step === "checking" && (
            <LoadingSpinner />
          )}

          {/* MEMBER FORM */}
          {step === "memberForm" && (
            <RegistrationForm
              eventId={eventId}
              eventData={eventData}
              isMember={true}
              formData={formData}
              formErrors={formErrors}
              error={error}
              isLoading={isLoading}
              updateForm={updateForm}
              handleFormSubmit={handleFormSubmit}
              addAccompanyingPerson={addAccompanyingPerson}
              removeAccompanyingPerson={removeAccompanyingPerson}
              updateAccompanyingPerson={updateAccompanyingPerson}
              setStep={setStep}
            />
          )}
          {/* GUEST FORM */}
          {step === "guestForm" && (
            <RegistrationForm
              eventId={eventId}
              eventData={eventData}
              isMember={false}
              formData={formData}
              formErrors={formErrors}
              error={error}
              isLoading={isLoading}
              updateForm={updateForm}
              handleFormSubmit={handleFormSubmit}
              addAccompanyingPerson={addAccompanyingPerson}
              removeAccompanyingPerson={removeAccompanyingPerson}
              updateAccompanyingPerson={updateAccompanyingPerson}
              setStep={setStep}
            />
          )}

          {/* PAYMENT */}
          {step === "payment" && (
            <PaymentView
              registration={registration}
              event={eventData}
              userData={userData}
              formData={formData}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              isPaying={isPaying}
              paymentConfirmed={paymentConfirmed}
              error={error}
              handlePayPalPayment={handlePayPalPayment}
              handleBankTransferConfirm={handleBankTransferConfirm}
              getTotalUSD={getTotalUSD}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
